import { type CSSProperties, type PointerEvent as ReactPointerEvent, useCallback, useState } from "react";

const STORAGE_PREFIX = "fraction:draggable-pos:";
const SIZE_STORAGE_PREFIX = "fraction:draggable-size:";

interface Pos {
  left: number;
  top: number;
}

interface Size {
  width: number;
  height: number;
}

interface DraggableOptions {
  /** localStorage key suffix — final key is `fraction:draggable-pos:<storageKey>`. */
  storageKey: string;
  /** Default position (used when nothing is in localStorage yet). */
  defaultPos: Pos;
  /** Width/height of the draggable element, used to clamp into the viewport. */
  width: number;
  height: number;
  /** Lower bound on resize (default 280×240). */
  minWidth?: number;
  minHeight?: number;
  /**
   * If set, drag only starts when the pointer-down originates inside an
   * element matching this CSS selector. Useful for limiting the drag handle
   * to a header rather than the entire body.
   */
  handleSelector?: string;
  /**
   * Selectors that should opt OUT of starting a drag — used to prevent
   * grabbing the modal when clicking a button inside the handle.
   */
  ignoreSelector?: string;
}

function readStored<T>(key: string, validate: (v: unknown) => v is T): T | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (validate(parsed)) return parsed;
  } catch {
    /* ignore — corrupted storage shouldn't crash the modal */
  }
  return null;
}

const isPos = (v: unknown): v is Pos =>
  !!v &&
  typeof (v as Pos).left === "number" &&
  typeof (v as Pos).top === "number" &&
  Number.isFinite((v as Pos).left) &&
  Number.isFinite((v as Pos).top);

const isSize = (v: unknown): v is Size =>
  !!v &&
  typeof (v as Size).width === "number" &&
  typeof (v as Size).height === "number" &&
  Number.isFinite((v as Size).width) &&
  Number.isFinite((v as Size).height);

function clamp(pos: Pos, width: number, height: number): Pos {
  if (typeof window === "undefined") return pos;
  return {
    left: Math.max(0, Math.min(window.innerWidth - width, pos.left)),
    top: Math.max(0, Math.min(window.innerHeight - height, pos.top)),
  };
}

function clampSize(size: Size, minWidth: number, minHeight: number): Size {
  if (typeof window === "undefined") return size;
  return {
    width: Math.max(minWidth, Math.min(window.innerWidth - 16, size.width)),
    height: Math.max(minHeight, Math.min(window.innerHeight - 16, size.height)),
  };
}

/**
 * Hook for positioning + sizing a fixed-position element via mouse/touch drag.
 * Returns a `style` to spread onto the element, an `onPointerDown` for the
 * drag handle, and an `onResizePointerDown` for a bottom-right resize grip.
 * Position and size are persisted to localStorage under the given key.
 */
export function useDraggablePosition({
  storageKey,
  defaultPos,
  width: defaultWidth,
  height: defaultHeight,
  minWidth = 280,
  minHeight = 240,
  handleSelector,
  ignoreSelector,
}: DraggableOptions) {
  const [size, setSize] = useState<Size>(() =>
    clampSize(
      readStored(SIZE_STORAGE_PREFIX + storageKey, isSize) ?? { width: defaultWidth, height: defaultHeight },
      minWidth,
      minHeight
    )
  );
  const [pos, setPos] = useState<Pos>(() =>
    clamp(readStored(STORAGE_PREFIX + storageKey, isPos) ?? defaultPos, size.width, size.height)
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      const target = e.target as HTMLElement;
      if (handleSelector && !target.closest(handleSelector)) return;
      if (ignoreSelector && target.closest(ignoreSelector)) return;
      e.preventDefault();

      const startX = e.clientX;
      const startY = e.clientY;
      const startPos = pos;
      const w = size.width;
      const h = size.height;
      let latest = startPos;

      const onMove = (ev: PointerEvent) => {
        latest = clamp(
          { left: startPos.left + (ev.clientX - startX), top: startPos.top + (ev.clientY - startY) },
          w,
          h
        );
        setPos(latest);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        try {
          localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(latest));
        } catch {
          /* full or disabled storage — drag still worked, just no persistence */
        }
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [pos, size.width, size.height, storageKey, handleSelector, ignoreSelector]
  );

  const persistSize = useCallback(
    (s: Size) => {
      try {
        localStorage.setItem(SIZE_STORAGE_PREFIX + storageKey, JSON.stringify(s));
      } catch {
        /* ignore */
      }
    },
    [storageKey]
  );

  const persistPos = useCallback(
    (p: Pos) => {
      try {
        localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(p));
      } catch {
        /* ignore */
      }
    },
    [storageKey]
  );

  // Bottom-right resize: extends width/height as the pointer moves down-right.
  const onResizePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const startSize = size;
      const startPos = pos;
      let latestSize = startSize;
      let latestPos = startPos;

      const onMove = (ev: PointerEvent) => {
        latestSize = clampSize(
          {
            width: Math.max(minWidth, startSize.width + (ev.clientX - startX)),
            height: Math.max(minHeight, startSize.height + (ev.clientY - startY)),
          },
          minWidth,
          minHeight
        );
        setSize(latestSize);
        // Re-clamp position so a resize-down near the edge doesn't push the
        // window off-screen.
        latestPos = clamp(startPos, latestSize.width, latestSize.height);
        setPos(latestPos);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        persistSize(latestSize);
        persistPos(latestPos);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [size, pos, minWidth, minHeight, persistSize, persistPos]
  );

  // Bottom-left corner resize: keeps the right edge anchored, grows/shrinks
  // width by shifting `left` and grows/shrinks height by extending downward.
  // Diagonal nesw-resize cursor.
  const onResizeBottomLeftPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const startSize = size;
      const startPos = pos;
      const rightAnchor = startPos.left + startSize.width;
      let latestSize = startSize;
      let latestPos = startPos;

      const onMove = (ev: PointerEvent) => {
        const desiredWidth = startSize.width - (ev.clientX - startX);
        const newWidth = Math.max(minWidth, Math.min(rightAnchor, desiredWidth));
        const newLeft = rightAnchor - newWidth;
        const newHeight = Math.max(minHeight, startSize.height + (ev.clientY - startY));
        latestSize = clampSize({ width: newWidth, height: newHeight }, minWidth, minHeight);
        latestPos = clamp({ left: newLeft, top: startPos.top }, latestSize.width, latestSize.height);
        setSize(latestSize);
        setPos(latestPos);
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        persistSize(latestSize);
        persistPos(latestPos);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [size, pos, minWidth, minHeight, persistSize, persistPos]
  );

  const style: CSSProperties = {
    left: pos.left,
    top: pos.top,
    width: size.width,
    height: size.height,
  };

  return { style, onPointerDown, onResizePointerDown, onResizeBottomLeftPointerDown };
}
